import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

import UsageHistoryTable, { UsageHistoryTableProps } from './UsageHistoryTable';
import BillingService from '@/components/Billing/billing-service';
import { DATE_FORMAT_DISPLAY } from '@/components/Billing/billing-service.const';
import { SubscriptionEvent, EntitlementObject, ProductType, FeatureTypes } from '@/components/Billing/billing-types';
import AppLoader from '@/react_app/common/AppLoader';
import AppPagination from '@/react_app/common/AppPagination';
import AnimateView from '@/react_app/components/BillingUsage/AnimateView';
import FeaturesCard from '@/react_app/components/BillingUsage/Overview/FeaturesCard';
import UsageHistoryChart from '@/react_app/components/BillingUsage/UsageHistory/UsageHistoryChart';
import { UsageHistoryTabProps } from '@/react_app/components/BillingUsage/UsageHistory/types';
import { addCommasToNumber } from '@/utils/helpers';

const EVENT_TYPE_TO_SENTENCE: Record<SubscriptionEvent, string> = {
	[SubscriptionEvent.Created]: 'Plan created',
	[SubscriptionEvent.CancelUpdates]: 'Plan scheduled updates cancelled',
	[SubscriptionEvent.ManuallyUpgraded]: 'Plan upgraded', //can be removed for prod
	[SubscriptionEvent.ManuallyCanceled]: 'Plan cancelled',
	[SubscriptionEvent.AutoUpgraded]: 'Plan upgraded (smart upgrade)',
	[SubscriptionEvent.AutoCancelUpdates]: 'Plan scheduled updates cancelled (smart upgrade)',
	[SubscriptionEvent.ManuallyChanged]: 'Plan changed manually',
	[SubscriptionEvent.ManuallyChangedUsersCount]: 'Users count changed',
};

interface SubscriptionHistoryItem {
	product_id: string;
	plan_id: string;
	created_at: string;
	unit_quantity: string;
	subscription_event: SubscriptionEvent;
	metered_entitlement_before: EntitlementObject;
	metered_entitlement_after: EntitlementObject;
	users_entitlements_before: Record<FeatureTypes, EntitlementObject>;
	users_entitlements_after: Record<FeatureTypes, EntitlementObject>;
}

const usersEntitlementCurrentUsage = (userEntitlement: Record<FeatureTypes, EntitlementObject>, featureType: FeatureTypes) => {
	const userEnt = userEntitlement?.[featureType];
	return addCommasToNumber(Number(userEnt?.entitlement?.current_usage || 0));
};

const usersEntitlementUsageLimit = (userEntitlement: Record<FeatureTypes, EntitlementObject>, featureType: FeatureTypes) => {
	const userEnt = userEntitlement?.[featureType];
	if (userEnt && userEnt.has_access && BillingService.isEntitlementUnlimited(userEnt)) {
		return 'unlimited';
	}
	return addCommasToNumber(Number(userEnt?.entitlement?.usage_limit || 0));
};

const getUsersUsage = (userEntitlement: Record<FeatureTypes, EntitlementObject>) => `premium: ${usersEntitlementCurrentUsage(
	userEntitlement,
	FeatureTypes.PremiumUsersCount
)},
standard: ${usersEntitlementCurrentUsage(userEntitlement, FeatureTypes.StandardUsersCount)}`;

const getUsersQuota = (userEntitlement: Record<FeatureTypes, EntitlementObject>) => `premium: ${usersEntitlementUsageLimit(
	userEntitlement,
	FeatureTypes.PremiumUsersCount
)},
standard: ${usersEntitlementUsageLimit(userEntitlement, FeatureTypes.StandardUsersCount)}`;

const UsageHistoryTab = ({
	cardDetails,
	productType,
	usageHistory,
	getFeatureUsageHistory,
	getSubscriptionHistory,
}: UsageHistoryTabProps) => {
	const [subscriptionHistoryResults, setSubscriptionHistoryResults] = useState<SubscriptionHistoryItem[]>([]);
	const [isLoadingFeatureUsageHistory, setIsLoadingFeatureUsageHistory] = useState(false);
	const tableData: UsageHistoryTableProps['data'] = useMemo(
		() =>
			subscriptionHistoryResults.map((item: SubscriptionHistoryItem, i) => {
				let usersUsage = null;
				let usersQuota = null;
				let quota = Number(item.unit_quantity || item.metered_entitlement_before.entitlement?.usage_limit || 0);
				const usage = Number(item.metered_entitlement_before.entitlement?.current_usage || 0);
				if (productType == ProductType.TeamFeatures) {
					quota = Number(item.metered_entitlement_after.entitlement?.usage_limit || 0);
					usersUsage = getUsersUsage(item.users_entitlements_after);
					usersQuota = getUsersQuota(item.users_entitlements_after);
				}
				const isCancellation = item.subscription_event === SubscriptionEvent.CancelUpdates;
				const isCreationEvent = item.subscription_event === SubscriptionEvent.Created;
				const showPlanName = !isCancellation && !isCreationEvent;
				const planName = showPlanName
					? BillingService.getPlanReadableName({ planId: item.plan_id, quantity: quota > 0 ? quota : undefined, productType })
					: '';
				return {
					key: String(i),
					plan: planName,
					quota: addCommasToNumber(quota),
					usage: addCommasToNumber(usage),
					usersUsage,
					usersQuota,
					date: moment(item.created_at).format(DATE_FORMAT_DISPLAY),
					event: EVENT_TYPE_TO_SENTENCE[item.subscription_event],
				};
			}),
		[subscriptionHistoryResults]
	);

	const chartData = useMemo(
		() =>
			usageHistory?.usage_measurements?.map((measurement) => ({
				date: moment(measurement.date).valueOf(),
				value: measurement.value,
				predicted: null,
			})) || [],
		[usageHistory]
	);

	const fetchUsageHistory = async () => {
		try {
			setIsLoadingFeatureUsageHistory(true);
			await getFeatureUsageHistory();
		} catch (e) {
			console.error('getFeatureUsageHistory  error:', e);
		} finally {
			setIsLoadingFeatureUsageHistory(false);
		}
	};

	useEffect(() => {
		fetchUsageHistory();
	}, []);

	return (
		<AnimateView>
			<FeaturesCard {...cardDetails} />
			<div className="mt-10" />
			{isLoadingFeatureUsageHistory ? <AppLoader /> : chartData.length > 0 ? <UsageHistoryChart data={chartData} /> : null}
			<AppPagination getData={getSubscriptionHistory} setResults={setSubscriptionHistoryResults}>
				<UsageHistoryTable data={tableData} productType={productType} />
			</AppPagination>
		</AnimateView>
	);
};

export default UsageHistoryTab;
